import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getAuthUrl, saveDraft, isGitHubAuthAvailable, publishWithToken, getStoredToken, setStoredToken, hasEnvToken } from '../lib/githubAuth';
import TuiEditor from '../components/TuiEditor';
import './WriteJekyllPost.css';

function slugify(text) {
  return text
    .toString()
    .normalize('NFC')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[.,\/#!$%\^&\*;:{}=\-_`~()"'’'""〈〉<>\[\]\|·…\u200B]/g, '')
    .replace(/--+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
}

function formatDate(dateStr) {
  const d = new Date(dateStr);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function WriteJekyllPost() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const editorRef = useRef(null);
  const [title, setTitle] = useState('');
  const [date, setDate] = useState(formatDate(new Date()));
  const [tags, setTags] = useState('');
  const [categories, setCategories] = useState('');
  const [downloaded, setDownloaded] = useState(false);
  const [publishResult, setPublishResult] = useState(null);
  const [githubPublishing, setGitHubPublishing] = useState(false);
  const [githubToken, setGitHubToken] = useState(getStoredToken());
  const [saveToken, setSaveToken] = useState(!!getStoredToken());

  const buildMarkdown = () => {
    const content = editorRef.current?.getInstance().getMarkdown() || '';
    const tagList = tags.split(',').map((t) => t.trim()).filter(Boolean);
    const catList = categories.split(',').map((c) => c.trim()).filter(Boolean);
    const slugVal = slugify(title || 'untitled');
    const yamlTags = tagList.length ? `\ntags: [${tagList.map((t) => t).join(', ')}]` : '';
    const yamlCats = catList.length ? `\ncategories: [${catList.map((c) => c).join(', ')}]` : '';
    const front = `---
title: "${(title || 'Untitled').replace(/"/g, '\\"')}"
date: ${date}
slug: ${slugVal}${yamlCats}${yamlTags}
---

`;
    return front + content;
  };

  const handleDownload = (e) => {
    e.preventDefault();
    const slug = slugify(title || 'untitled');
    const content = buildMarkdown();
    const filename = `${date}-${slug}.md`;
    const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    setDownloaded(true);
  };

  const handlePublishWithToken = async (e) => {
    e.preventDefault();
    const slug = slugify(title || 'untitled');
    const content = buildMarkdown();
    if (!title?.trim()) {
      setPublishResult({ error: '제목을 입력해 주세요.' });
      return;
    }
    setGitHubPublishing(true);
    setPublishResult(null);
    try {
      setStoredToken(githubToken.trim(), saveToken);
      const result = await publishWithToken(
        { title: title.trim(), date, content, slug },
        githubToken.trim()
      );
      setPublishResult(result);
    } catch (err) {
      setPublishResult({ error: err?.message || '게시에 실패했습니다.' });
    }
    setGitHubPublishing(false);
  };

  const handlePublishWithGitHubOAuth = async (e) => {
    e.preventDefault();
    const slug = slugify(title || 'untitled');
    const content = buildMarkdown();
    if (!title?.trim()) {
      alert('제목을 입력해 주세요.');
      return;
    }
    setGitHubPublishing(true);
    try {
      saveDraft({ title: title.trim(), date, content, slug });
      const authUrl = await getAuthUrl();
      if (!authUrl) {
        setPublishResult({
          error: 'OAuth를 쓰려면 .env에 REACT_APP_GITHUB_OAUTH_CLIENT_ID를 설정하세요. 또는 아래 "토큰으로 게시"를 사용하세요.',
        });
      } else {
        window.location.href = authUrl;
        return;
      }
    } catch (err) {
      setPublishResult({ error: err?.message || 'GitHub 로그인 준비에 실패했습니다.' });
    }
    setGitHubPublishing(false);
  };

  const isAdmin = user?.email === 'calicorone@gmail.com';
  if (!isAdmin) {
    return (
      <div className="write-jekyll-wrap">
        <p className="write-jekyll-unauthorized">접근 권한이 없습니다.</p>
      </div>
    );
  }

  return (
    <div className="write-jekyll-wrap">
      <div className="write-jekyll-inner">
        <header className="write-jekyll-header">
          <h1 className="write-jekyll-title">blog.koiro.me 글쓰기</h1>
          <p className="write-jekyll-desc">
            GitHub 토큰으로 바로 게시하거나, .md 파일을 다운로드해 수동으로 올릴 수 있습니다.
          </p>
        </header>

        <form className="write-jekyll-form" onSubmit={(e) => e.preventDefault()}>
          <label className="write-jekyll-label">제목 (필수)</label>
          <input
            type="text"
            className="write-jekyll-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="글 제목을 입력하세요"
            required
          />
          <label className="write-jekyll-label">날짜</label>
          <input
            type="date"
            className="write-jekyll-input"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
          <label className="write-jekyll-label">태그 (쉼표로 구분)</label>
          <input
            type="text"
            className="write-jekyll-input"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="roadmap, dev, cursor"
          />
          <label className="write-jekyll-label">카테고리 (쉼표로 구분, 선택)</label>
          <input
            type="text"
            className="write-jekyll-input"
            value={categories}
            onChange={(e) => setCategories(e.target.value)}
            placeholder="roadmap"
          />
          <label className="write-jekyll-label">본문 (마크다운)</label>
          <p className="write-jekyll-desc" style={{ marginBottom: '0.5rem', fontSize: '0.85rem' }}>
            마크다운만 작성해도 됩니다. 제목·날짜·태그 등은 자동으로 프론트 매터에 들어가며, 별도 레이아웃 없이 글이 그대로 반영됩니다.
          </p>
          <div className="write-jekyll-editor">
            <TuiEditor
              ref={editorRef}
              height="400px"
              initialEditType="markdown"
              previewStyle="vertical"
            />
          </div>
          <label className="write-jekyll-label" style={{ marginTop: '1rem' }}>
            GitHub 토큰 (repo 권한)
          </label>
          {hasEnvToken() && (
            <p className="write-jekyll-desc" style={{ marginBottom: '0.5rem', fontSize: '0.9rem' }}>
              .env 또는 Cloudflare 등에 설정된 <code>REACT_APP_GITHUB_PUBLISH_TOKEN</code> 사용 중. 아래에 넣으면 입력값이 우선합니다.
            </p>
          )}
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="password"
              className="write-jekyll-input"
              value={githubToken}
              onChange={(e) => setGitHubToken(e.target.value)}
              placeholder={hasEnvToken() ? '(환경 변수 사용 시 비워 두기)' : 'ghp_... 또는 github_pat_... (repo 권한)'}
              style={{ flex: '1', minWidth: '200px' }}
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', whiteSpace: 'nowrap' }}>
              <input type="checkbox" checked={saveToken} onChange={(e) => setSaveToken(e.target.checked)} />
              세션에 저장
            </label>
          </div>
          <p className="write-jekyll-desc" style={{ marginTop: '0.25rem', fontSize: '0.85rem' }}>
            <a href="https://github.com/settings/tokens" target="_blank" rel="noopener noreferrer">GitHub → Settings → Developer settings → Personal access tokens</a>에서 repo 권한 토큰을 만든 뒤 위 입력란에 붙여넣으세요.
            {!hasEnvToken() && (
              <> .env에 <code>REACT_APP_GITHUB_PUBLISH_TOKEN=토큰</code>으로 저장해도 됩니다.</>
            )}
          </p>
          <div className="write-jekyll-actions">
            <button type="button" className="write-jekyll-cancel" onClick={() => navigate('/dev-blog')}>
              취소
            </button>
            <button type="button" className="write-jekyll-download" onClick={handleDownload}>
              {downloaded ? '다시 다운로드' : '.md 파일 다운로드'}
            </button>
            <button
              type="button"
              className="write-jekyll-publish write-jekyll-github-btn"
              onClick={handlePublishWithToken}
              disabled={githubPublishing || !title?.trim()}
            >
              {githubPublishing ? '게시 중…' : 'GitHub 토큰으로 게시'}
            </button>
            {isGitHubAuthAvailable() && (
              <button
                type="button"
                className="write-jekyll-cancel"
                onClick={handlePublishWithGitHubOAuth}
                disabled={githubPublishing || !title?.trim()}
              >
                OAuth로 게시
              </button>
            )}
          </div>
          {!title?.trim() && (
            <p className="write-jekyll-required-hint">제목을 입력하면 게시 버튼이 활성화됩니다.</p>
          )}
        </form>

        {publishResult?.ok && (
          <p className="write-jekyll-success">
            게시되었습니다. <a href={publishResult.url} target="_blank" rel="noopener noreferrer">blog.koiro.me에서 보기</a> (반영까지 1–2분 걸릴 수 있습니다).
          </p>
        )}
        {publishResult?.error && (
          <p className="write-jekyll-error">
            {publishResult.error}
            {publishResult.detail && <small>{publishResult.detail}</small>}
          </p>
        )}
        {downloaded && !publishResult && (
          <p className="write-jekyll-hint">
            다운로드한 파일을 <strong>koiro-dev-blog/content/posts/</strong> 폴더에 넣고 GitHub에 푸시하면 수동으로 게시할 수 있습니다. (블로그는 Hugo라 마크다운 + 프론트 매터만 맞으면 됩니다.)
          </p>
        )}
      </div>
    </div>
  );
}

export default WriteJekyllPost;
